<template>
  <div>
    <div>
      <button
        :aria-labelledby="`question-${promptId} button-${promptId}`"
        class="textarea-overview w-100 text-left"
        v-if="!editorHasText"
        @click="showModal"
      >
        <span :id="`button-${promptId}`">{{ trans("community.write_a_think") }}</span>
      </button>
      <div v-else>
        <div class="text-view d-flex flex-row">
          <div v-if="moodSaved" v-html="moodSaved.icon" class="pr-2" />
          <div>&ndash;</div>
          <div v-html="text" class="pl-2" />
        </div>
        <div class="d-flex justify-content-end">
          <b-button v-if="legacyPage" variant="outline-secondary" @click="showModal">{{
            trans("courses.edit")
          }}</b-button>
        </div>
      </div>
    </div>
    <b-modal :id="promptId" size="lg" scrollable no-close-on-esc>
      <template v-slot:modal-title>
        <h4 class="text-secondary mt-md-5 mt-3" aria-hidden="true">{{ question }}</h4>
      </template>
      <b-overlay :show="isLoading" spinner-variant="secondary" spinner-type="grow" spinner-small>
        <div class="editor-wrapper px-4">
          <ckeditor :config="editorConfig" v-model="textValue" @ready="onEditorReady" />
        </div>
      </b-overlay>
      <template v-slot:modal-footer>
        <div
          class="d-flex w-100"
          :class="
            !moodDisabled
              ? showDropdown
                ? 'justify-content-between'
                : 'justify-content-end'
              : 'justify-content-end'
          "
        >
          <emotions
            v-if="showDropdown && !moodDisabled"
            :identity="Number(promptId)"
            :emotion-list="activeMoodsList"
            :selected-emotion="mood.id ? mood.id : null"
            :button-text="buttonValue"
            classes="w-100 emotion-list"
            @emotionChange="dropdownChange($event)"
            @buttonClicked="dropdownClicked"
          />
          <b-button @click="$bvModal.hide(promptId)">{{ trans("courses.ok") }}</b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Vue from "vue";
import CKEditor from "ckeditor4-vue";
import Emotions from "../molecules/Emotions";
import { mapGetters } from "vuex";
Vue.use(CKEditor);

export default {
  name: "FullScreenEditor",
  components: { Emotions },
  props: {
    blockId: Number,
    blockType: String,
    question: String,
    promptId: String,
    pageId: String,
    textContent: String,
    moodContent: Number,
    moodDisabled: Boolean,
  },
  data() {
    return {
      isLoading: true,
      text: this.textContent,
      textValue: this.textContent,
      mood: {
        id: null,
        value: {
          icon: null,
          text: null,
        },
      },
      editorConfig: {
        toolbar: [
          { name: "links", items: ["EmojiPanel"] },
          { name: "basicstyles", items: ["Bold", "Italic"] },
        ],
        startupFocus: true,
        extraPlugins: "emoji, editorplaceholder, font",
        editorplaceholder: this.trans("courses.write_a_think"),
        contentsCss: "/css/text-editor.css",
      },
      dropdownButton: "",
    };
  },
  methods: {
    showModal() {
      this.$bvModal.show(this.promptId);
    },

    submit() {
      //enter if there was an answer saved (text) OR there is a new answer (textValue) not empty
      if (this.text || this.cleanText) {
        if (this.cleanText) {
          this.text = this.textValue;
        } else this.text = "";
        this.$emit("submit", {
          answers: this.text,
          mood: this.mood.id ? this.mood.id : null,
        });
      }
    },
    onEditorReady() {
      this.isLoading = false;
    },

    dropdownChange(mood) {
      this.mood.id = mood.id;
      this.mood.value.icon = mood.icon;
      this.mood.value.text = mood.text;

      trackEvent("MoodSelect", {
        "prompt-id": this.promptId,
        "element-type": this.currentElementType,
        "element-id": this.currentElementId,
        "mood-id": mood.id,
        "mood-icon": mood.icon,
        "mood-name": mood.text,
      });
    },

    dropdownClicked() {
      trackEvent("AddMood", {
        "prompt-id": this.promptId,
        "element-type": this.currentElementType,
        "element-id": this.currentElementId,
      });
    },
    moodValid(valueToCheck) {
      return [null, this.moodsList.notSelected].indexOf(valueToCheck) === -1;
    },
  },
  mounted() {
    this.$root.$on("bv::modal::hide", (bvEvent, promptId) => {
      if (promptId === this.promptId) {
        this.submit();
      }
    });

    if (this.moodContent) this.mood.id = this.moodContent;
  },
  computed: {
    ...mapGetters({
      moodsList: "moodsAndReactions/getMoodsList",
      activeMoodsList: "moodsAndReactions/getActiveMoodsList",
      userId: "user/getUserId",
      currentElementType: "streams/getCurrentElementType",
      currentElementId: "streams/getCurrentElementId",
      getMood: "moodsAndReactions/getMood",
    }),
    editorHasText: function () {
      if (!this.text || (this.text && this.text.length === 0)) return false;
      else return true;
    },
    legacyPage: function () {
      if (this.pageId) return true;
      else return false;
    },
    hasMoodsList() {
      return this.moodsList.moods.length > 0;
    },
    moodSaved: function () {
      if (this.moodValid(this.mood.id))
        return this.moodsList.moods.find((m) => m.id === this.mood.id);
      else return null;
    },
    defaultDropdownButton: function () {
      return `${this.moodsList.dropdown.icon} <small>${this.moodsList.dropdown.name}</small>`;
    },
    buttonValue() {
      if (this.moodValid(this.mood.id)) {
        let emotion = this.getMood(this.mood.id);
        return `${emotion.icon} ${emotion.name}`;
      } else return this.trans("mood-reactions.emotion_button_v2");
    },
    cleanText() {
      return this.textValue
        .replace(/(<p>){1}(\S){0}((&nbsp;){1}(\s)*)+(\S){0}(<\/p>){1}/g, "")
        .replace(/\r|\n/g, "");
    },
    showDropdown() {
      return this.textValue || this.text;
    },
  },
};
</script>

<style scoped lang="scss">
.editor-wrapper {
  position: relative;
  height: 100% !important;
  .cke_contents {
    overflow: hidden;
  }

  &.light-blue {
    background: $color-light;
  }

  ::v-deep div,
  ::v-deep .ProseMirror {
    height: 100% !important;
    &:focus {
      outline: none;
    }
  }

  .placeholder {
    position: absolute;
    opacity: 0.6;
  }
}

.modal-textarea {
  border: none;
  background: transparent !important;
}

.textarea-overview {
  color: initial;
  background: rgba($color-white, 0.4);
  height: 54px !important;
  border: 1px solid rgba($color-dark-600, 0.4);
  color: rgba($color-dark, 0.4);
  border-radius: 0.5rem;
  padding: 0.375rem 0.75rem;
  overflow-y: auto;
  line-height: 38px;
  outline: none;
}

::v-deep .text-view {
  p {
    margin-bottom: 0.5rem;
    @media (max-width: 991px) {
      margin-bottom: 0.25rem;
    }
  }
}

::v-deep .modal-dialog {
  height: 680px;
  display: flex;

  .modal-content {
    @media (max-width: 991px) {
      .modal-header {
        .modal-title {
          h4 {
            font-size: 1rem;
            line-height: 1.2;
          }
        }
      }
    }
  }

  .modal-footer {
    padding: 1.5rem;
  }

  @media (max-width: 991px) {
    height: 100%;
    margin: 0;
    padding: 0;

    .modal-content {
      border: none;
      border-radius: 0px;
      height: 100%;
      overflow: auto;
      max-height: 100%;
    }
    &.modal-dialog-scrollable {
      max-height: 100%;
    }
  }

  .modal-body {
    margin-bottom: 16px;
    overflow: visible;
    padding-left: 0;
    padding-right: 0;
    padding-top: 0;
  }

  .cke_wysiwyg_frame {
    background-color: $color-white;
  }

  .cke_bottom {
    display: none;
  }
  .cke {
    border: none;
  }
  .cke_top {
    position: absolute;
    bottom: -80px;
    left: 28px;
    border: none;
    background: none;
    padding-left: 0;
  }
  .b-overlay-wrap {
    position: absolute !important;
    height: 100%;
    width: 100%;
  }
  a.cke_button {
    border: 2px solid $color-blue;
    border-radius: 100%;
    width: 20px;
    height: 20px;
    display: inline-block;
    background: none;
    line-height: 22px;
    margin: 0 8px 0 0;
    padding: 0;
    cursor: pointer;

    &:hover {
      padding: 0;
    }
    &.cke_button_on {
      border-color: $color-orange;
      .cke_button_icon:after,
      .cke_button__emojipanel_icon:before {
        color: $color-orange;
        border-color: $color-orange;
      }
      &:before {
        color: $color-orange;
      }
    }
    .cke_button_icon {
      background: none !important;
      text-align: center;
      &:after {
        color: $color-blue;
        font-size: 14px;
        line-height: 18px;
      }
    }
    .cke_button__bold_icon {
      &:after {
        content: "b";
        font-weight: bold;
      }
    }
    .cke_button__italic_icon {
      &:after {
        content: "i";
        font-style: italic;
      }
    }
  }
  .cke_toolgroup a.cke_button:last-child:after,
  .cke_button_arrow {
    display: none !important;
  }
  .cke_toolgroup {
    margin-right: 0;
    padding-right: 0;
  }
  .cke_button__emojipanel {
    position: relative;
    width: 100%;
    height: 100%;
    &:before {
      content: "..";
      position: absolute;
      top: -11px;
      left: 2px;
      color: $color-blue;
      font-size: 28px;
    }
    .cke_button__emojipanel_icon:before {
      content: "";
      position: absolute;
      top: 6px;
      left: 6px;
      display: block;
      width: 8px;
      height: 10px;
      border-radius: 100%;
      border-bottom: 3px solid $color-blue;
    }
  }
}

@media (max-width: 991px) {
  //to hide intercom button on Mobile
  div[id$="___BV_modal_outer_"] {
    z-index: 2200000000 !important;
  }

  ::v-deep .modal-dialog {
    @media (min-width: 576px) {
      max-width: 100%;
    }
    .cke_button__emojipanel {
      display: none !important;
    }
  }
}
</style>
